import { AllCameraImage, CameraImage } from 'types/CarTracking';
import { getFormattedDateAndTime } from 'utils/helpers';
import type { CameraImageLabel, CarouselImage } from './CameraImageCard';

export const CAMERA_IMAGES_EMPTY_MESSAGE = 'Camera images will show up here.';

export const CAMERA_VIEW_LAYOUT_VARIANTS = {
    MIXED: 'mixed',
    AERIAL_ONLY: 'aerialOnly',
    TRIPWIRE_ONLY: 'tripwireOnly',
} as const;
export type CameraViewLayoutVariant = (typeof CAMERA_VIEW_LAYOUT_VARIANTS)[keyof typeof CAMERA_VIEW_LAYOUT_VARIANTS];

export const CAMERA_TOP_SECTION_LAYOUT_CONTEXTS = {
    SPLIT: 'split',
    FULL_WIDTH: 'fullWidth',
} as const;
export type CameraTopSectionLayoutContext =
    (typeof CAMERA_TOP_SECTION_LAYOUT_CONTEXTS)[keyof typeof CAMERA_TOP_SECTION_LAYOUT_CONTEXTS];
export const CAMERA_VIEW_RENDER_MODES = {
    MIXED_SPLIT: 'mixedSplit',
    MIXED_FULL_WIDTH: 'mixedFullWidth',
    AERIAL_ONLY: 'aerialOnly',
    TRIPWIRE_COLUMN: 'tripwireColumn',
    TRIPWIRE_ROW: 'tripwireRow',
} as const;
export type CameraViewRenderMode = (typeof CAMERA_VIEW_RENDER_MODES)[keyof typeof CAMERA_VIEW_RENDER_MODES];
export type TripwireFeed = {
    key: 'entrance' | 'exit';
    images: CarouselImage[];
};

export function buildCameraLabels(cameraLabel: string, eventDatetimeSeconds: number): CameraImageLabel[] {
    return [
        { content: cameraLabel, position: { top: 10, left: 10 } },
        {
            content: `Last updated: ${getFormattedDateAndTime(new Date(eventDatetimeSeconds * 1000))}`,
            position: { bottom: 10, left: 10 },
        },
    ];
}

export function toCarouselImage(src: string, cameraLabel: string, eventDatetimeSeconds: number): CarouselImage {
    return {
        src,
        fallbackSrc: src,
        labels: buildCameraLabels(cameraLabel, eventDatetimeSeconds),
    };
}

function resolveCameraImageSources(image: CameraImage): { preferredSrc: string; fallbackSrc: string } {
    const fallbackSrc = image.image_url;
    return {
        preferredSrc: image.thumbnail_url || fallbackSrc,
        fallbackSrc,
    };
}

export function toCarouselImages(images: CameraImage[], cameraLabel: string): CarouselImage[] {
    return images.map((image) => {
        const { preferredSrc, fallbackSrc } = resolveCameraImageSources(image);
        return {
            src: preferredSrc,
            fallbackSrc,
            labels: buildCameraLabels(cameraLabel, image.event_datetime),
        };
    });
}

export function toGridCardImage(image: AllCameraImage): CarouselImage {
    const fallbackSrc = image.image_url;
    return {
        src: image.thumbnail_url || fallbackSrc,
        fallbackSrc,
        labels: buildCameraLabels(image.camera_type, image.event_datetime),
    };
}

export function getCameraViewLayoutVariant(
    hasAerialImages: boolean,
    hasTripwireImages: boolean
): CameraViewLayoutVariant {
    if (hasAerialImages && hasTripwireImages) {
        return CAMERA_VIEW_LAYOUT_VARIANTS.MIXED;
    }
    if (hasAerialImages) {
        return CAMERA_VIEW_LAYOUT_VARIANTS.AERIAL_ONLY;
    }
    return CAMERA_VIEW_LAYOUT_VARIANTS.TRIPWIRE_ONLY;
}

export function getCameraViewRenderMode(
    layoutVariant: CameraViewLayoutVariant,
    topSectionLayoutContext: CameraTopSectionLayoutContext
): CameraViewRenderMode {
    if (layoutVariant === CAMERA_VIEW_LAYOUT_VARIANTS.AERIAL_ONLY) {
        return CAMERA_VIEW_RENDER_MODES.AERIAL_ONLY;
    }

    if (layoutVariant === CAMERA_VIEW_LAYOUT_VARIANTS.MIXED) {
        return topSectionLayoutContext === CAMERA_TOP_SECTION_LAYOUT_CONTEXTS.FULL_WIDTH
            ? CAMERA_VIEW_RENDER_MODES.MIXED_FULL_WIDTH
            : CAMERA_VIEW_RENDER_MODES.MIXED_SPLIT;
    }

    return topSectionLayoutContext === CAMERA_TOP_SECTION_LAYOUT_CONTEXTS.FULL_WIDTH
        ? CAMERA_VIEW_RENDER_MODES.TRIPWIRE_ROW
        : CAMERA_VIEW_RENDER_MODES.TRIPWIRE_COLUMN;
}

export function getTripwireFeeds(entranceImages: CarouselImage[], exitImages: CarouselImage[]): TripwireFeed[] {
    const feeds: TripwireFeed[] = [];

    if (entranceImages.length > 0) {
        feeds.push({ key: 'entrance', images: entranceImages });
    }

    if (exitImages.length > 0) {
        feeds.push({ key: 'exit', images: exitImages });
    }

    return feeds;
}
